<template>
  <button
    class="theme-toggle"
    @click="toggleTheme"
    :aria-label="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
    :title="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
  >
    <!-- Sun icon (shown in dark mode - click to switch to light) -->
    <svg
      v-if="isDark"
      class="theme-toggle__icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <circle cx="12" cy="12" r="5"></circle>
      <line x1="12" y1="1" x2="12" y2="3"></line>
      <line x1="12" y1="21" x2="12" y2="23"></line>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
      <line x1="1" y1="12" x2="3" y2="12"></line>
      <line x1="21" y1="12" x2="23" y2="12"></line>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
    </svg>
    <!-- Moon icon (shown in light mode - click to switch to dark) -->
    <svg
      v-else
      class="theme-toggle__icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
    </svg>
  </button>
</template>

<script>
export default {
  name: 'ThemeToggle',
  data() {
    return {
      isDark: false
    };
  },
  mounted() {
    this.initializeTheme();

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', this.handleSystemThemeChange);
  },
  beforeUnmount() {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.removeEventListener('change', this.handleSystemThemeChange);
  },
  methods: {
    initializeTheme() {
      // Check for saved theme preference
      const savedTheme = localStorage.getItem('autooperator-theme');

      if (savedTheme) {
        // Use saved preference
        this.isDark = savedTheme === 'dark';
      } else {
        // Fall back to system preference
        this.isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }

      this.applyTheme();
    },
    handleSystemThemeChange(e) {
      // Only respond to system changes if user hasn't set a preference
      if (!localStorage.getItem('autooperator-theme')) {
        this.isDark = e.matches;
        this.applyTheme();
      }
    },
    toggleTheme() {
      this.isDark = !this.isDark;
      localStorage.setItem('autooperator-theme', this.isDark ? 'dark' : 'light');
      this.applyTheme();

      // Emit event for any listeners
      this.$emit('theme-changed', this.isDark ? 'dark' : 'light');
    },
    applyTheme() {
      document.documentElement.setAttribute('data-theme', this.isDark ? 'dark' : 'light');

      // Update meta theme-color for mobile browsers
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', this.isDark ? '#1e1e1e' : '#ffffff');
      }
    }
  }
};
</script>

<style scoped>
.theme-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 150ms ease;
}

.theme-toggle:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
  border-color: var(--border-strong);
}

.theme-toggle:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--bg-primary), 0 0 0 4px var(--color-primary, #00897b);
}

.theme-toggle:active {
  transform: scale(0.95);
}

.theme-toggle__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* Compact variant for navigation */
.theme-toggle--compact {
  width: 36px;
  height: 36px;
}

.theme-toggle--compact .theme-toggle__icon {
  width: 18px;
  height: 18px;
}
</style>
